import paper from "paper";
import type { Vec2 } from "./Project";
import {
  computeOffsetPathData,
  computeSingleSegmentOffsetFromPathData,
} from "./constructionOffset";

/**
 * Geometry for offsetting a stitch seam parallel to a leather part's edge
 * (#768). Given a part outline (`piece.shapePathData`) and an inset distance,
 * produce the `Vec2[]` points of a seam that runs inside the part — either the
 * whole outline (a closed ring) or just one grabbed edge (an open run). Reuses
 * the construction offset engine (`computeOffsetPathData` /
 * `computeSingleSegmentOffsetFromPathData`) so the parallel-curve math stays in
 * one place; this module only adapts it from construction lines to part
 * outlines and samples the result into stitch points.
 */

/** Target segment length (mm) when sampling a curved offset result into a
 *  stitch polyline — mirrors StitchTool's `SHAPE_SAMPLE_MM`. */
const SAMPLE_MM = 2;
/** Probe distance (mm) used to decide which offset sign points inward. */
const INWARD_PROBE_MM = 3;

export interface OutlineEdge {
  /** Subpath index within the outline CompoundPath. */
  pathIndex: number;
  /** Curve index within that subpath. */
  curveIndex: number;
  /** Nearest point on the edge to the query point. */
  point: Vec2;
  /** Distance from the query point to that nearest point. */
  dist: number;
}

/** Parse a pathData string into its subpaths (a lone Path is treated as one). */
function subpathsOf(
  pathData: string,
  scope: paper.PaperScope,
): { compound: paper.CompoundPath; subs: paper.Path[] } | null {
  let compound: paper.CompoundPath;
  try {
    compound = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return null;
  }
  const subs =
    compound.children && compound.children.length > 0
      ? (compound.children as paper.Path[])
      : [compound as unknown as paper.Path];
  return { compound, subs };
}

/** The longest subpath of a parsed offset result — the whole-outline offset can
 *  fragment into disjoint runs at tight corners, and a seam is a single
 *  polyline, so keep the dominant run. */
function longestSubpath(subs: paper.Path[]): paper.Path | null {
  let best: paper.Path | null = null;
  for (const s of subs) {
    if (!s || s.length <= 0) continue;
    if (!best || s.length > best.length) best = s;
  }
  return best;
}

/**
 * The outline edge closest to `pt`, within `tolMm`, across the part's subpaths —
 * or null when nothing is near enough. Detects "the press landed on an edge"
 * and identifies which single edge the Alt shortcut should offset. Mirrors
 * `ConstructionTool.hitConstructionCurve` but reads a part's `shapePathData`.
 */
export function nearestOutlineEdge(
  piecePathData: string,
  pt: Vec2,
  scope: paper.PaperScope,
  tolMm: number,
): OutlineEdge | null {
  const parsed = subpathsOf(piecePathData, scope);
  if (!parsed) return null;
  const q = new scope.Point(pt.x, pt.y);
  let best: OutlineEdge | null = null;
  for (let pi = 0; pi < parsed.subs.length; pi++) {
    const sub = parsed.subs[pi];
    if (!sub || !sub.curves || sub.curves.length === 0) continue;
    const loc = sub.getNearestLocation(q);
    if (!loc) continue;
    const d = q.getDistance(loc.point);
    if (d <= tolMm && (!best || d < best.dist)) {
      best = {
        pathIndex: pi,
        curveIndex: loc.curve.index,
        point: { x: loc.point.x, y: loc.point.y },
        dist: d,
      };
    }
  }
  parsed.compound.remove();
  return best;
}

/** Perpendicular distance from `pt` to a specific outline edge (the grabbed
 *  segment), which sets how far the offset seam insets. Null if the edge is
 *  gone or the outline won't parse. */
export function distanceToEdge(
  piecePathData: string,
  segment: { pathIndex: number; curveIndex: number },
  pt: Vec2,
  scope: paper.PaperScope,
): number | null {
  const parsed = subpathsOf(piecePathData, scope);
  if (!parsed) return null;
  const sub = parsed.subs[segment.pathIndex];
  const curve = sub?.curves?.[segment.curveIndex] ?? null;
  if (!curve) {
    parsed.compound.remove();
    return null;
  }
  const loc = curve.getNearestLocation(new scope.Point(pt.x, pt.y));
  const d = loc ? new scope.Point(pt.x, pt.y).getDistance(loc.point) : null;
  parsed.compound.remove();
  return d;
}

/** The nearest point on the grabbed edge to `pt` — the perpendicular foot the
 *  offset distance ray (#789) springs from. Null if the edge is gone or the
 *  outline won't parse. */
export function nearestPointOnEdge(
  piecePathData: string,
  segment: { pathIndex: number; curveIndex: number },
  pt: Vec2,
  scope: paper.PaperScope,
): Vec2 | null {
  const parsed = subpathsOf(piecePathData, scope);
  if (!parsed) return null;
  const sub = parsed.subs[segment.pathIndex];
  const curve = sub?.curves?.[segment.curveIndex] ?? null;
  if (!curve) {
    parsed.compound.remove();
    return null;
  }
  const loc = curve.getNearestLocation(new scope.Point(pt.x, pt.y));
  const foot = loc ? { x: loc.point.x, y: loc.point.y } : null;
  parsed.compound.remove();
  return foot;
}

/**
 * Resolved unit vectors for the Offset seam kind's hover affordance (#148 /
 * #768): `dir` points the way the seam will inset from the grabbed edge — INTO
 * the part for an outline (`wantInside`), OUT into the surrounding leather for a
 * cutout / hole (`!wantInside`) — and `tan` runs parallel to the edge (the seam
 * tick). Computed from the edge's tangent at the nearest point to `pt`, with the
 * interior side resolved by a `contains` probe. Null if the edge is gone, the
 * outline won't parse, or the tangent is degenerate.
 */
export function offsetHoverGeometry(
  piecePathData: string,
  segment: { pathIndex: number; curveIndex: number },
  pt: Vec2,
  scope: paper.PaperScope,
  wantInside: boolean,
): { dirX: number; dirY: number; tanX: number; tanY: number } | null {
  const parsed = subpathsOf(piecePathData, scope);
  if (!parsed) return null;
  const sub = parsed.subs[segment.pathIndex];
  const curve = sub?.curves?.[segment.curveIndex] ?? null;
  if (!curve) {
    parsed.compound.remove();
    return null;
  }
  const loc = curve.getNearestLocation(new scope.Point(pt.x, pt.y));
  if (!loc || !loc.tangent || loc.tangent.length < 1e-9) {
    parsed.compound.remove();
    return null;
  }
  const tan = loc.tangent.normalize();
  // Left-of-direction normal in y-down space; probe just off the edge to learn
  // which side is the outline's interior.
  const leftNormal = new scope.Point(tan.y, -tan.x);
  const eps = 0.05;
  const interiorIsLeft = parsed.compound.contains(
    loc.point.add(leftNormal.multiply(eps)),
  );
  const interior = interiorIsLeft ? leftNormal : leftNormal.multiply(-1);
  const dir = wantInside ? interior : interior.multiply(-1);
  parsed.compound.remove();
  return { dirX: dir.x, dirY: dir.y, tanX: tan.x, tanY: tan.y };
}

/** Whether a representative point of `offsetPathData` lands inside the source
 *  outline — used to pick the inward offset sign. */
function offsetLandsInside(
  offsetPathData: string,
  outline: paper.CompoundPath,
  scope: paper.PaperScope,
): boolean {
  const parsed = subpathsOf(offsetPathData, scope);
  if (!parsed) return false;
  const sub = longestSubpath(parsed.subs);
  const mid = sub ? sub.getPointAt(sub.length / 2) : null;
  const inside = mid ? outline.contains(mid) : false;
  parsed.compound.remove();
  return inside;
}

/**
 * The sign (`+1` / `-1`) to multiply an inset distance by so the offset moves to
 * the wanted side of the source path — INSIDE it (`wantInside`, e.g. a part
 * outline insetting into the leather) or OUTSIDE it (`!wantInside`, e.g. a
 * cutout / hole edge offsetting out into the leather). Winding direction decides
 * which sign the offset engine treats as inward, so probe both with a small
 * distance and keep whichever lands on the wanted side. `segment` picks the
 * single-edge convention; pass null for the whole outline. Defaults sensibly if
 * neither probe resolves.
 */
export function offsetSign(
  piecePathData: string,
  scope: paper.PaperScope,
  segment: { pathIndex: number; curveIndex: number } | null,
  wantInside: boolean,
): number {
  const parsed = subpathsOf(piecePathData, scope);
  if (!parsed) return wantInside ? -1 : 1;
  const outline = parsed.compound;
  const probe = (d: number): boolean => {
    const data = segment
      ? computeSingleSegmentOffsetFromPathData(piecePathData, d, segment, scope)
      : computeOffsetPathData(piecePathData, d, scope);
    if (!data) return false;
    return offsetLandsInside(data, outline, scope) === wantInside;
  };
  const sign = probe(-INWARD_PROBE_MM) ? -1 : probe(INWARD_PROBE_MM) ? 1 : -1;
  outline.remove();
  return sign;
}

/** Sample a paper path into stitch points, PER EDGE rather than uniformly along
 *  the whole path. A straight edge contributes only its two endpoints; a curved
 *  edge is arc-length sampled at `SAMPLE_MM`. So the straight sides of a
 *  rounded rectangle, a slot/pill, or any shape mixing lines and arcs stay as
 *  clean corners while only the genuinely curved runs densify — matching how
 *  construction offsets keep their corners in pathData (the stitch model is a
 *  point list, so we mirror that structure by walking curves).
 *
 *  A whole-outline offset that is all straight edges (square / polygon) reduces
 *  to just its corner vertices; a fully curved outline (ellipse) still samples
 *  end to end. For a closed ring the trailing point coincident with the start
 *  is dropped (StitchLine re-adds the implicit closing segment); an open run
 *  keeps both endpoints. #970 fix — the earlier all-or-nothing `hasHandles()`
 *  gate densified a whole shape whenever ANY edge curved, so mixed shapes
 *  produced dozens of points along their straight sides. */
function samplePath(path: paper.Path, closed: boolean): Vec2[] {
  if (path.length <= 0) return [];
  const curves = path.curves;
  if (!curves || curves.length === 0) {
    return path.segments.map((s) => ({ x: s.point.x, y: s.point.y }));
  }
  const pts: Vec2[] = [];
  const push = (x: number, y: number) => {
    const prev = pts[pts.length - 1];
    if (!prev || Math.hypot(prev.x - x, prev.y - y) > 1e-6) pts.push({ x, y });
  };
  const firstCurve = curves[0]!;
  push(firstCurve.point1.x, firstCurve.point1.y);
  for (const c of curves) {
    // `isStraight()` also catches curves whose handles are collinear (a line
    // with non-zero but aligned handles) — endpoints are still exact there.
    if (c.isStraight()) {
      push(c.point2.x, c.point2.y);
      continue;
    }
    const clen = c.length;
    const steps = Math.max(1, Math.round(clen / SAMPLE_MM));
    for (let i = 1; i <= steps; i++) {
      const loc = c.getLocationAt((i / steps) * clen);
      if (loc) push(loc.point.x, loc.point.y);
    }
  }
  // A closed ring's final curve returns to the start anchor; drop that
  // duplicate so the sampled ring matches the "closing segment implicit"
  // convention StitchLine expects.
  if (closed && pts.length > 1) {
    const first = pts[0]!;
    const last = pts[pts.length - 1]!;
    if (Math.hypot(first.x - last.x, first.y - last.y) <= 1e-6) pts.pop();
  }
  return pts;
}

/** Sample an offset pathData's dominant run into stitch points. */
function pointsFromOffset(
  offsetPathData: string,
  closed: boolean,
  scope: paper.PaperScope,
): Vec2[] {
  const parsed = subpathsOf(offsetPathData, scope);
  if (!parsed) return [];
  const sub = longestSubpath(parsed.subs);
  const pts = sub ? samplePath(sub, closed) : [];
  parsed.compound.remove();
  return pts;
}

/**
 * Offset the whole part outline by `signedInset` (magnitude = inset distance,
 * sign from {@link inwardSign}) and sample it into a closed seam ring. Null when
 * the offset collapses or the outline won't parse.
 */
export function offsetWholeToPoints(
  piecePathData: string,
  signedInset: number,
  scope: paper.PaperScope,
  /** #423: `roundedCorners: false` keeps an outward offset's corners
   *  square instead of arcing them. Ignored for an outline with curves
   *  in it, which has no sharp corners to keep. */
  options?: { roundedCorners?: boolean },
): { points: Vec2[]; closed: true } | null {
  if (!isFinite(signedInset) || signedInset === 0) return null;
  const data = computeOffsetPathData(
    piecePathData,
    signedInset,
    scope,
    options,
  );
  if (!data) return null;
  const points = pointsFromOffset(data, true, scope);
  return points.length >= 3 ? { points, closed: true } : null;
}

/**
 * Offset just the grabbed edge by `signedInset` and sample it into an open seam
 * run (parallel to that one edge). Null when the edge is gone or the offset
 * won't parse.
 */
export function offsetSegmentToPoints(
  piecePathData: string,
  signedInset: number,
  segment: { pathIndex: number; curveIndex: number },
  scope: paper.PaperScope,
): { points: Vec2[]; closed: false } | null {
  if (!isFinite(signedInset) || signedInset === 0) return null;
  const data = computeSingleSegmentOffsetFromPathData(
    piecePathData,
    signedInset,
    segment,
    scope,
  );
  if (!data) return null;
  const points = pointsFromOffset(data, false, scope);
  return points.length >= 2 ? { points, closed: false } : null;
}
